import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Button,
  useTheme,
  alpha,
  Divider,
  ListItemIcon,
  ListItemText,
  Chip,
  Drawer,
  List,
  ListItemButton,
  useMediaQuery,
} from '@mui/material';
import {
  AccountCircle,
  Settings,
  Logout,
  Business,
  KeyboardArrowDown,
  Menu as MenuIcon,
  Close as CloseIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
} from '@mui/icons-material';
import { useAppearance } from '../theme/AppearanceProvider';

interface CompactAppBarProps {
  title: string;
  userEmail: string;
  currentTenant?: string;
  onSignOut: () => void;
  onNavigate?: (path: string) => void;
  navigationLinks?: Array<{ label: string; path: string; active?: boolean }>;
  tenantSelector?: React.ReactNode;
  /**
   * Optional extra content rendered inside the user menu header
   * (under the username/email block). Intended for small per-page actions.
   */
  userMenuExtra?: React.ReactNode;
}

/**
 * CompactAppBar - Modern, space-efficient app bar following Material UI v6 best practices
 *
 * Features:
 * - Single-line layout with integrated navigation
 * - Responsive menu for smaller screens
 * - User account menu with dropdown
 * - Inline tenant selector
 * - Minimal padding for maximum space efficiency
 */
export const CompactAppBar: React.FC<CompactAppBarProps> = ({
  title,
  userEmail,
  currentTenant,
  onSignOut,
  // onNavigate kept in interface for backward compatibility but now unused - navigation uses Link
  navigationLinks = [],
  tenantSelector,
  userMenuExtra,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const appearance = useAppearance();
  const appearanceRef = React.useRef(appearance);
  appearanceRef.current = appearance;

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleSignOut = () => {
    handleUserMenuClose();
    setMobileDrawerOpen(false);
    onSignOut();
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        bgcolor:
          theme.palette.mode === 'dark'
            ? theme.palette.background.paper
            : theme.palette.primary.main,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          minHeight: { xs: '56px', sm: '48px' }, // Taller on mobile for better touch targets
          px: { xs: 1, sm: 2 },
          gap: { xs: 1, sm: 2 },
        }}
      >
        {/* Mobile Menu Button - shown only on mobile when nav links exist */}
        {isMobile && navigationLinks.length > 0 && (
          <IconButton
            color="inherit"
            aria-label="open navigation menu"
            onClick={handleMobileDrawerToggle}
            sx={{
              minWidth: 44,
              minHeight: 44,
              mr: 0.5,
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* App Title with subtle emphasis */}
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontSize: { xs: '1rem', sm: '1.1rem' },
            fontWeight: 600,
            whiteSpace: 'nowrap',
            color: 'inherit',
          }}
        >
          {title}
        </Typography>

        {/* Navigation Links - hidden on mobile (shown in drawer instead) */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            gap: 0.5,
            alignItems: 'center',
          }}
        >
          {navigationLinks.map((link) => (
            <Button
              key={link.path}
              component={Link}
              to={link.path}
              sx={{
                color: 'inherit',
                textTransform: 'none',
                textDecoration: 'none',
                fontSize: '0.875rem',
                px: 1.5,
                py: 0.5,
                minWidth: 'auto',
                borderRadius: 1,
                bgcolor: link.active ? alpha(theme.palette.common.white, 0.15) : 'transparent',
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                },
              }}
            >
              {link.label}
            </Button>
          ))}
        </Box>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Tenant Selector (if provided) */}
        {tenantSelector ||
          (currentTenant && (
            <Chip
              icon={<Business sx={{ fontSize: '0.875rem' }} />}
              label={currentTenant}
              size="small"
              sx={{
                height: '28px',
                bgcolor: alpha(theme.palette.common.white, 0.15),
                color: 'inherit',
                fontWeight: 500,
                fontSize: '0.8125rem',
                '& .MuiChip-icon': {
                  color: 'inherit',
                  opacity: 0.8,
                },
              }}
            />
          ))}

        {/* User Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontSize: '0.8125rem',
              color: 'inherit',
              opacity: 0.9,
            }}
          >
            {userEmail.split('@')[0]}
          </Typography>
          <IconButton
            size="small"
            onClick={handleUserMenuOpen}
            sx={{
              color: 'inherit',
              // Ensure minimum 44px touch target on mobile
              minWidth: { xs: 44, sm: 'auto' },
              minHeight: { xs: 44, sm: 'auto' },
              p: { xs: 1.25, sm: 0.5 },
              '&:hover': {
                bgcolor: alpha(theme.palette.common.white, 0.15),
              },
            }}
            aria-label="account menu"
            aria-controls="user-menu"
            aria-haspopup="true"
          >
            <AccountCircle fontSize="small" />
            <KeyboardArrowDown sx={{ fontSize: '1rem', ml: -0.5 }} />
          </IconButton>
        </Box>

        {/* User Menu Dropdown */}
        <Menu
          id="user-menu"
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          slotProps={{
            paper: {
              elevation: 3,
              sx: {
                mt: 1,
                minWidth: 200,
                borderRadius: 1.5,
              },
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5, pb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {userEmail.split('@')[0]}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {userEmail}
            </Typography>
            {userMenuExtra ? <Box sx={{ mt: 1 }}>{userMenuExtra}</Box> : null}
          </Box>
          <Divider />
          <MenuItem onClick={handleUserMenuClose}>
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            <ListItemText>Settings</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              const { mode, setMode } = appearanceRef.current;
              setMode(mode === 'dark' ? 'light' : 'dark');
            }}
          >
            <ListItemIcon>
              {appearanceRef.current.mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>
              {appearanceRef.current.mode === 'dark' ? 'Light mode (slate)' : 'Dark mode (denim)'}
            </ListItemText>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem onClick={handleSignOut}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            <ListItemText>Sign Out</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={handleMobileDrawerToggle}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
            bgcolor: 'background.paper',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Navigation
          </Typography>
          <IconButton onClick={handleMobileDrawerToggle} aria-label="close navigation">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List sx={{ pt: 1 }}>
          {navigationLinks.map((link) => (
            <ListItemButton
              key={link.path}
              component={Link}
              to={link.path}
              selected={link.active}
              onClick={handleMobileDrawerToggle}
              sx={{
                py: 1.5,
                px: 2,
                minHeight: 48, // Touch-friendly
                '&.Mui-selected': {
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.18),
                  },
                },
              }}
            >
              <ListItemText
                primary={link.label}
                primaryTypographyProps={{
                  fontWeight: link.active ? 600 : 400,
                  color: link.active ? 'primary.main' : 'text.primary',
                }}
              />
            </ListItemButton>
          ))}
        </List>
        <Divider sx={{ mt: 'auto' }} />
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Signed in as
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {userEmail}
          </Typography>
        </Box>
      </Drawer>
    </AppBar>
  );
};
